import React, { useState, useEffect } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Edit2, Trash2, GripVertical } from 'lucide-react';
import { formatInTimeZone } from 'date-fns-tz';
import { getHolidayForDate } from '@/utils/holidays';
import { useAuth } from '@/context/AuthContext';

const SortableWrapper = ({ clockId, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: clockId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return children({ setNodeRef, style, attributes, listeners });
};

const ClockCardContent = ({
  clock,
  format24hr,
  onEdit,
  onDelete,
  weekendMode,
  holidays,
  setNodeRef,
  style,
  attributes,
  listeners,
}) => {
  const { isAdmin } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const timeFormat = format24hr ? 'HH:mm:ss' : 'hh:mm:ss a';
  const dateFormat = 'EEE · MMM d';

  const formattedTime = formatInTimeZone(currentTime, clock.timezone, timeFormat);
  const formattedDate = formatInTimeZone(currentTime, clock.timezone, dateFormat);

  const hour = parseInt(formatInTimeZone(currentTime, clock.timezone, 'H'), 10);
  const dayOfWeek = parseInt(formatInTimeZone(currentTime, clock.timezone, 'i'), 10);
  const isWeekend = dayOfWeek === 6 || dayOfWeek === 7;
  const workStart = clock.workStartHour ?? 9;
  const workEnd = clock.workEndHour ?? 19;
  const holiday = getHolidayForDate(currentTime, clock.timezone, holidays);
  const isBusinessHours = hour >= workStart && hour < workEnd && !isWeekend && !holiday;
  const shouldDim = weekendMode && isWeekend;

  // Status text + color
  let statusLabel = 'off hours';
  let statusColor = 'text-zinc-500';
  let statusDot = 'bg-zinc-600';
  if (holiday) {
    statusLabel = `holiday · ${holiday.name}`;
    statusColor = 'text-rose-300';
    statusDot = 'bg-rose-400';
  } else if (isWeekend) {
    statusLabel = 'weekend';
    statusColor = 'text-amber-300';
    statusDot = 'bg-amber-400';
  } else if (isBusinessHours) {
    statusLabel = 'in office';
    statusColor = 'text-emerald-300';
    statusDot = 'bg-emerald-400';
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid="clock-card"
      className={`relative border-r border-b border-white/[0.08] p-6 sm:p-7 group transition-colors hover:bg-white/[0.015] ${shouldDim ? 'opacity-40' : ''}`}
    >
      {/* Action bar — admin only */}
      {isAdmin && (
        <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1.5 hover:bg-white/10 text-white/40 hover:text-white/80 transition-colors"
            data-testid="clock-drag-handle"
            title="Drag to reorder"
          >
            <GripVertical className="w-4 h-4" />
          </button>
          <Button
            size="icon"
            variant="ghost"
            data-testid="clock-edit-btn"
            onClick={() => onEdit(clock)}
            className="h-7 w-7 rounded-none hover:bg-white/10 text-white/40 hover:text-[#735AE2]"
          >
            <Edit2 className="w-3.5 h-3.5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            data-testid="clock-delete-btn"
            onClick={() => onDelete(clock.id)}
            className="h-7 w-7 rounded-none hover:bg-white/10 text-white/40 hover:text-rose-400"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}

      {/* Status row */}
      <div className="flex items-center gap-2 mb-5">
        <span className={`w-1.5 h-1.5 rounded-full ${statusDot} ${isBusinessHours ? 'now-pulse' : ''}`}></span>
        <span className={`font-mono text-[10px] uppercase tracking-[0.2em] ${statusColor}`} data-testid={holiday ? 'holiday-indicator' : isWeekend ? 'weekend-indicator' : isBusinessHours ? 'business-hours-indicator' : 'status-indicator'}>
          {statusLabel}
        </span>
      </div>

      {/* Avatar + Member name */}
      <div className="flex items-center gap-3 mb-5">
        {clock.avatar ? (
          <img
            src={clock.avatar}
            alt={clock.memberName || clock.customName}
            className="w-10 h-10 object-cover border border-white/[0.12]"
            data-testid="clock-avatar"
          />
        ) : (
          <div className="w-10 h-10 border border-white/[0.12] flex items-center justify-center bg-white/[0.02]">
            <span className="font-mono text-[10px] font-bold text-[#9D8AEC]">
              {(clock.memberName || clock.customName).slice(0, 2).toUpperCase()}
            </span>
          </div>
        )}
        <div className="min-w-0 flex-1">
          {clock.memberName && (
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#735AE2] truncate" data-testid="clock-member-name">
              {clock.memberName}
            </div>
          )}
          <div className="font-heading text-sm font-semibold text-white truncate" data-testid="clock-name">
            {clock.customName}
          </div>
        </div>
      </div>

      {/* Big time */}
      <div className="font-mono text-2xl sm:text-3xl font-semibold text-white tracking-tight tabular-nums" data-testid="clock-time">
        {formattedTime}
      </div>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1" data-testid="clock-date">
        {formattedDate}
      </div>

      {/* Working hours footer */}
      <div className="mt-5 pt-3 border-t border-white/[0.06] font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600 flex items-center justify-between" data-testid="clock-work-hours">
        <span>works {workStart.toString().padStart(2, '0')}:00–{workEnd.toString().padStart(2, '0')}:00</span>
      </div>
    </div>
  );
};

const ClockCard = ({ clock, format24hr, onEdit, onDelete, isSortable = true, weekendMode = false, holidays = {} }) => {
  if (!isSortable) {
    return (
      <ClockCardContent
        clock={clock}
        format24hr={format24hr}
        onEdit={onEdit}
        onDelete={onDelete}
        weekendMode={weekendMode}
        holidays={holidays}
        setNodeRef={null}
        style={{}}
        attributes={{}}
        listeners={{}}
      />
    );
  }

  return (
    <SortableWrapper clockId={clock.id}>
      {({ setNodeRef, style, attributes, listeners }) => (
        <ClockCardContent
          clock={clock}
          format24hr={format24hr}
          onEdit={onEdit}
          onDelete={onDelete}
          weekendMode={weekendMode}
          holidays={holidays}
          setNodeRef={setNodeRef}
          style={style}
          attributes={attributes}
          listeners={listeners}
        />
      )}
    </SortableWrapper>
  );
};

export default ClockCard;
